import { computed } from 'vue';

import { keyPermissionsEnabled, unifiedMutationsEnabled } from '@/config/unifiedMutations';
import { useAuthStore } from '@/stores/auth';
import { useEnvironmentStore } from '@/stores/environment';

// The one definition of the super-admin dev-only override: a super admin who has switched on
// Dev Settings -> "Show dev-only pages" sees unreleased features on any build (prod/stage).
// Plain function so non-setup callers (the router guard) can use it with the stores they hold.
export const isDevOnlyOverrideActive = (authStore, envStore) => !!authStore?.user?.superAdmin && !!envStore?.showDevOnlyPages;

// The one definition of "dev-only pages and nav items are shown": in a development environment
// anyone sees them while the switch is on (its default there); anywhere else only a super admin
// who switched it on. Used by the nav filter and the router guard (isDevOnlyPathAllowed).
// A store without the switch (showDevOnlyPages undefined) counts as the development default, on.
export const isDevOnlyPagesVisible = (authStore, envStore) =>
  envStore?.isDevelopment ? envStore.showDevOnlyPages !== false : isDevOnlyOverrideActive(authStore, envStore);

// Reactive form for components. Build flags stay the default; the override only ever adds.
export const useDevOnlyOverride = () => {
  const authStore = useAuthStore();
  const envStore = useEnvironmentStore();

  const devOnlyOverride = computed(() => isDevOnlyOverrideActive(authStore, envStore));
  const keyPermissionsActive = computed(() => keyPermissionsEnabled || devOnlyOverride.value);
  const unifiedMutationsActive = computed(() => unifiedMutationsEnabled || devOnlyOverride.value);

  return { devOnlyOverride, keyPermissionsActive, unifiedMutationsActive };
};
